import { Form, Formik } from 'formik';
import { Props } from './CompanyForm.types';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import Button from '../../components/button/Button';
import { useTranslation } from '../../context/translationContext';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useLoading } from '../../context/loadingContext';
import http from '../../api/axios';
import { useEffect, useState } from 'react';
import { apiUrl } from '../../api/apiUrl';
import { handleApiErrors } from '../../api/apiErrorHandler';

export const CompanyForm = ({ onSubmit, initialValues }: Props) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading, loading } = useLoading();
  const [data, setData] = useState<any | null>(null);
  const [initialValuesLoc, setInitialValuesLoc] = useState<any | null>(initialValues);

  const companySchema = Yup.object().shape({
    name: Yup.string().required('Company name is required.'),
  });

  /**
   * Loads data by companyId
   */
  const fetchDataByCompanyId = async (companyId: string) => {
    try {
      startLoading();
      const response = await http.apiGet({
        url: `${apiUrl.COMPANY_SEARCH_BY_ID}?companyId=${companyId}`,
      });
      setData(response?.data);
      stopLoading();
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  useEffect(() => {
    if (!initialValues || initialValues?.id === initialValuesLoc?.id) return;
    const tmpInitialValues = {
      id: initialValues?.id ?? undefined,
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      supplierAddressLine1: initialValues?.supplierAddressLine1 ?? '',
      supplierAddressLine2: initialValues?.supplierAddressLine2 ?? '',
      supplierAddressCity: initialValues?.supplierAddressCity ?? '',
      supplierAddressPostCode: initialValues?.supplierAddressPostCode ?? '',
      supplierAddressCountry: initialValues?.supplierAddressCountry ?? '',
      supplierCompanyId: initialValues?.supplierCompanyId ?? '',
      supplierVatId: initialValues?.supplierVatId ?? '',
      maxDecimalNumUnitPrice: initialValues?.maxDecimalNumUnitPrice ?? 2,
      codeBookItemsUseCode: initialValues?.codeBookItemsUseCode ?? false,
    };

    setInitialValuesLoc(tmpInitialValues);
  }, [initialValues]);

  /**
   * Search in ARES by company id
   */
  const handleSearchCompany = (companyId: string) => {
    fetchDataByCompanyId(companyId);
  };

  /**
   * Company in ARES found --> fill data to inputs
   */
  useEffect(() => {
    if (!data) return;
    const tmpInitialValues = {
      ...initialValues,
      name: data.name ?? '',
      description: data.description ?? '',
      supplierAddressLine1: data.supplierAddressLine1 ?? '',
      supplierAddressLine2: data.supplierAddressLine2 ?? '',
      supplierAddressCity: data.supplierAddressCity ?? '',
      supplierAddressPostCode: data.supplierAddressPostCode ?? '',
      supplierAddressCountry: data.supplierAddressCountry ?? '',
      supplierCompanyId: data.supplierCompanyId ?? '',
      supplierVatId: data.supplierVatId ?? '',
      // default values
      maxDecimalNumUnitPrice: 2,
      codeBookItemsUseCode: false,
    };
    setInitialValuesLoc(tmpInitialValues);
  }, [data]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValuesLoc}
      onSubmit={onSubmit}
      validationSchema={companySchema}
    >
      {({ errors, touched, values }) => (
        <Form className="w-full">
          <div className="flex flex-row ">
            <div className="w-2/5">
              <div className="my-4">
                <FormField
                  id="name"
                  name="name"
                  label={t('page.company.input-company-name')}
                  errors={errors}
                  touched={touched}
                />
              </div>
              <div className="my-4">
                <FormField
                  id="description"
                  name="description"
                  label={t('page.company.input-company-desc')}
                />
              </div>
              <div className="my-4 w-full">
                <div className="flex">
                  <div className="w-full">
                    <FormField
                      id="supplierCompanyId"
                      name="supplierCompanyId"
                      label={t('page.company.input-company-id')}
                      disabled={loading}
                    />
                  </div>
                  <div className="ml-1">
                    <Button
                      onClick={(e: any) => {
                        e.preventDefault();
                        handleSearchCompany(values.supplierCompanyId);
                      }}
                      disabled={loading}
                      className="!px-1.5"
                      icon={<MagnifyingGlassIcon className="h-5 w-5 text-lg	 cursor-pointer" />}
                    />
                  </div>
                </div>
              </div>
              <div className="my-4">
                <FormField
                  id="supplierVatId"
                  name="supplierVatId"
                  label={t('page.company.input-vat-id')}
                />
              </div>
              <div className="my-4 flex flex-row items-center">
                <label htmlFor={'codeBookItemsUseCode'} className="mr-5">
                  {t('page.company.iput-code-book-items-use')}
                </label>

                <FormField
                  name={'codeBookItemsUseCode'}
                  id={'codeBookItemsUseCode'}
                  type="checkbox"
                  checked={values?.codeBookItemsUseCode}
                  className={`w-4 h-4`}
                />
              </div>
              <div className="my-4 flex flex-row items-center">
                <label htmlFor={'maxDecimalNumUnitPrice'} className="mr-5">
                  {t('page.company.input-max-decimal-num-unit-price')}
                </label>
                <FormField
                  type="select"
                  id="maxDecimalNumUnitPrice"
                  name="maxDecimalNumUnitPrice"
                  value={values.maxDecimalNumUnitPrice ?? 2}
                >
                  {Array.from({ length: 10 }).map((_, index: number) => (
                    <option key={index} value={index + 1}>
                      {index + 1}
                    </option>
                  ))}
                </FormField>
              </div>
            </div>
            <div className="ml-5 w-3/5">
              <div className="my-4">
                <FormField
                  id="supplierAddressLine1"
                  name="supplierAddressLine1"
                  label={t('page.company.input-address-1')}
                />
              </div>
              <div className="my-4">
                <FormField
                  id="supplierAddressLine2"
                  name="supplierAddressLine2"
                  label={t('page.company.input-address-2')}
                />
              </div>
              <div className="my-4">
                <FormField
                  id="supplierAddressCity"
                  name="supplierAddressCity"
                  label={t('page.company.input-address-city')}
                />
              </div>
              <div className="my-4">
                <FormField
                  id="supplierAddressPostCode"
                  name="supplierAddressPostCode"
                  label={t('page.company.input-address-zip')}
                />
              </div>
              <div className="my-4">
                <FormField
                  id="supplierAddressCountry"
                  name="supplierAddressCountry"
                  label={t('page.company.input-address-country')}
                />
              </div>
            </div>
          </div>
          <div>
            <Button title={t('general.save')} type="submit" color="green" showLoading />
          </div>
        </Form>
      )}
    </Formik>
  );
};
